import React, { useContext, useEffect, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { TwButton, TwButtonType } from '../../../../../components/TwButtons/TwButtons';
import SettingsService from '../../../../../providers/Settings/Settings.service';
import { NotificationsContext } from '../../../../../providers/Notifications/Notifications.provider';
import TwCheckbox from '../../../../../components/TwCheckboxes/TwCheckboxes';
import './LoadNotificationOptions.scss';
import {
  LoadNotificationRecipients,
  LoadNotificationSettingsRequest,
  LoadNotificationStatusEvents,
  LoadNotificationStopStatusEvents,
  LoadNotificationStopTypes
} from '../../../../../providers/Settings/LoadNotificationSettings.model';
import { useGetOrderNotificationSettingsQuery } from '../../../../../redux/api/orders/ordersApi';

export interface LoadNotificationSettingsValues {
  id?: string;
  order_id?: string | null;
  recipients: LoadNotificationRecipients;
  order_status_events: LoadNotificationStatusEvents;
  stop_status_events: LoadNotificationStopStatusEvents;
  stop_types: LoadNotificationStopTypes;
  use_global_settings?: boolean;
  notify_customer_when_status_changes?: boolean;
}

interface LoadNotificationSettingsProps {
  /** Handles settings saved in backend */
  handleOriginalSettings?: (settings: LoadNotificationSettingsValues) => void;
  /** Handles settings selected in UI */
  handleSelectedSettings: (settings: LoadNotificationSettingsValues) => void;
  /** Load settings edited during create/edit load */
  temporarySettings?: LoadNotificationSettingsValues;
  /** Load id needed if editing or saving existing load settings */
  loadId?: string;
  /** Discards changes and goes back to backend settings */
  discardChanges?: (discard: boolean) => void;
  /** Discarded changes */
  discarded?: boolean;
  /** used on settings page */
  isSettingsPage: boolean;
}

const LoadNotificationOptions: React.FC<LoadNotificationSettingsProps> = ({
  handleOriginalSettings,
  handleSelectedSettings,
  temporarySettings,
  discardChanges,
  discarded,
  loadId,
  isSettingsPage
}): JSX.Element => {
  const { addNotification } = useContext(NotificationsContext);
  const { data: _orderNotificationSettings, refetch: refreshOrderNotifications } =
    useGetOrderNotificationSettingsQuery({}, { skip: !isSettingsPage });

  const [orderNotificationSettings, setOrderNotificationSettings] =
    useState<LoadNotificationSettingsValues>({
      id: '',
      order_id: null,
      recipients: {
        customer_contact_email: false,
        customer_billing_email: false,
        user_dispatching_order: false,
        dispatcher_assigned_on_order: false,
        additional_emails: []
      },
      order_status_events: {
        created: false,
        in_transit: false,
        delivered: false
      },
      stop_status_events: {
        in_transit: false,
        arrived: false,
        complete: false
      },
      stop_types: {
        pickup: false,
        drop_off: false,
        transfer: false,
        none: false
      }
    });

  useEffect(() => {
    if (_orderNotificationSettings && isSettingsPage && !temporarySettings) {
      handleOriginalSettings &&
        handleOriginalSettings({
          id: _orderNotificationSettings.id,
          order_id: null,
          recipients: _orderNotificationSettings.recipients,
          order_status_events: _orderNotificationSettings.order_status_events,
          stop_status_events: _orderNotificationSettings.stop_status_events,
          stop_types: _orderNotificationSettings.stop_types
        });
      setOrderNotificationSettings({
        id: _orderNotificationSettings.id,
        order_id: null,
        recipients: _orderNotificationSettings.recipients,
        order_status_events: _orderNotificationSettings.order_status_events,
        stop_status_events: _orderNotificationSettings.stop_status_events,
        stop_types: _orderNotificationSettings.stop_types
      });
      discardChanges && discardChanges(false);
    }
  }, [_orderNotificationSettings]);

  /**
   * Get saved load notification settings (general or for specific load)
   */
  const getSavedOrderNotificationSettings = (): void => {
    SettingsService.getOrderNotificationSettings(loadId ? loadId : '')
      .then((loadSettings) => {
        handleOriginalSettings &&
          handleOriginalSettings({
            id: loadSettings.id,
            recipients: loadSettings.recipients,
            order_status_events: loadSettings.order_status_events,
            stop_status_events: loadSettings.stop_status_events,
            stop_types: loadSettings.stop_types
          });
        setOrderNotificationSettings({
          id: loadSettings.id,
          recipients: loadSettings.recipients,
          order_status_events: loadSettings.order_status_events,
          stop_status_events: loadSettings.stop_status_events,
          stop_types: loadSettings.stop_types
        });
        discardChanges && discardChanges(false);
      })
      .catch((error) => {
        addNotification({
          message: error.message,
          variant: 'error'
        });
      });
  };

  useEffect(() => {
    discarded && refreshOrderNotifications();
  }, [discarded]);

  useEffect(() => {
    if (temporarySettings) {
      setOrderNotificationSettings({
        ...orderNotificationSettings,
        id: temporarySettings.id,
        order_status_events: temporarySettings.order_status_events,
        stop_status_events: temporarySettings.stop_status_events,
        stop_types: temporarySettings.stop_types,
        recipients: temporarySettings.recipients
      });
    } else {
      getSavedOrderNotificationSettings();
    }
  }, [temporarySettings]);

  useEffect(() => {
    handleSelectedSettings && handleSelectedSettings(orderNotificationSettings);
  }, [orderNotificationSettings]);

  /**
   * Handles the values update for the Recipients settings
   */
  const handleRecipientsCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: LoadNotificationRecipients | undefined = {
      ...orderNotificationSettings.recipients
    };
    switch (e.target.name) {
      case 'customer_contact_email':
        checkboxToUpdate = {
          ...orderNotificationSettings.recipients,
          customer_contact_email: e.target.checked
        };
        break;
      case 'customer_billing_email':
        checkboxToUpdate = {
          ...orderNotificationSettings.recipients,
          customer_billing_email: e.target.checked
        };
        break;
      case 'user_dispatching_load':
        checkboxToUpdate = {
          ...orderNotificationSettings.recipients,
          user_dispatching_order: e.target.checked
        };
        break;
      case 'dispatcher_assigned_on_order':
        checkboxToUpdate = {
          ...orderNotificationSettings.recipients,
          dispatcher_assigned_on_order: e.target.checked
        };
        break;
    }

    const updated = {
      ...orderNotificationSettings,
      recipients: {
        ...checkboxToUpdate
      }
    };
    setOrderNotificationSettings(updated);
  };

  /**
   * Handles the values update for the Load status settings
   */
  const handleLoadStatusCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: LoadNotificationStatusEvents | undefined = {
      ...orderNotificationSettings.order_status_events
    };

    switch (e.target.name) {
      case 'load_created':
        checkboxToUpdate = {
          ...orderNotificationSettings.order_status_events,
          created: e.target.checked
        };
        break;
      case 'load_in_transit':
        checkboxToUpdate = {
          ...orderNotificationSettings.order_status_events,
          in_transit: e.target.checked
        };
        break;
      case 'load_delivered':
        checkboxToUpdate = {
          ...orderNotificationSettings.order_status_events,
          delivered: e.target.checked
        };
        break;
    }

    const updated = {
      ...orderNotificationSettings,
      order_status_events: {
        ...checkboxToUpdate
      }
    };
    setOrderNotificationSettings(updated);
  };

  /**
   * Handles the values update for the Stop status settings
   */
  const handleStopStatusCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: LoadNotificationStopStatusEvents | undefined = {
      ...orderNotificationSettings.stop_status_events
    };
    switch (e.target.name) {
      case 'stop_in_transit':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_status_events,
          in_transit: e.target.checked
        };
        break;
      case 'stop_arrived':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_status_events,
          arrived: e.target.checked
        };
        break;
      case 'stop_complete':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_status_events,
          complete: e.target.checked
        };
        break;
    }

    const updated = {
      ...orderNotificationSettings,
      stop_status_events: {
        ...checkboxToUpdate
      }
    };
    setOrderNotificationSettings(updated);
  };

  /**
   * Handles the values update for the Stop type settings
   */
  const handleStopTypeCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    let checkboxToUpdate: LoadNotificationStopTypes | undefined = {
      ...orderNotificationSettings.stop_types
    };
    switch (e.target.name) {
      case 'pickup_stops':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_types,
          pickup: e.target.checked
        };
        break;
      case 'drop_off_stops':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_types,
          drop_off: e.target.checked
        };
        break;
      case 'transfer_stops':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_types,
          transfer: e.target.checked
        };
        break;
      case 'no_type_stops':
        checkboxToUpdate = {
          ...orderNotificationSettings.stop_types,
          none: e.target.checked
        };
        break;
    }

    const updated = {
      ...orderNotificationSettings,
      stop_types: {
        ...checkboxToUpdate
      }
    };
    setOrderNotificationSettings(updated);
  };

  return (
    <Box className="load-notifications-options">
      <Stack className="load-notifications-options__options-container">
        <Typography variant="h5" className="load-notifications-options__options-title">
          {`Default recipients`}
        </Typography>
        <Typography className="settings__subtitle">
          Select the default recipients who will receive status update emails for orders
        </Typography>
        <TwCheckbox
          name="customer_contact_email"
          value="customer_contact_email"
          label="Customer contact email"
          checked={orderNotificationSettings.recipients.customer_contact_email}
          onInputChange={handleRecipientsCheckboxChange}
        />
        <TwCheckbox
          name="customer_billing_email"
          value="customer_billing_email"
          label="Customer billing email"
          checked={orderNotificationSettings.recipients.customer_billing_email}
          onInputChange={handleRecipientsCheckboxChange}
        />
        <TwCheckbox
          name="user_dispatching_load"
          value="user_dispatching_load"
          label="User who created the order"
          checked={orderNotificationSettings.recipients.user_dispatching_order}
          onInputChange={handleRecipientsCheckboxChange}
        />
        <TwCheckbox
          name="dispatcher_assigned_on_order"
          value="dispatcher_assigned_on_order"
          label="Dispatcher assigned on order"
          checked={orderNotificationSettings.recipients.dispatcher_assigned_on_order}
          onInputChange={handleRecipientsCheckboxChange}
        />
      </Stack>
      <Stack className="load-notifications-options__options-container">
        <Typography variant="h5" className="load-notifications-options__options-title">
          {`Select when status emails should be sent`}
        </Typography>
        <Typography className="settings__subtitle">{`Send a status update email when an order is:`}</Typography>
        <TwCheckbox
          name="load_created"
          value="load_created"
          label="Created"
          onInputChange={handleLoadStatusCheckboxChange}
          checked={orderNotificationSettings.order_status_events.created}
        />
        <TwCheckbox
          name="load_in_transit"
          value="load_in_transit"
          label="Marked In-transit"
          onInputChange={handleLoadStatusCheckboxChange}
          checked={orderNotificationSettings.order_status_events.in_transit}
        />
        <TwCheckbox
          name="load_delivered"
          value="load_delivered"
          label="Marked Delivered"
          onInputChange={handleLoadStatusCheckboxChange}
          checked={orderNotificationSettings.order_status_events.delivered}
        />
      </Stack>
      <Stack className="load-notifications-options__options-container">
        <Typography className="settings__subtitle">{`Send a status update email when a stop is:`}</Typography>
        <TwCheckbox
          name="stop_in_transit"
          value="stop_in_transit"
          label="Marked en Route"
          onInputChange={handleStopStatusCheckboxChange}
          checked={orderNotificationSettings.stop_status_events.in_transit}
        />
        <TwCheckbox
          name="stop_arrived"
          value="stop_arrived"
          label="Marked Arrived"
          onInputChange={handleStopStatusCheckboxChange}
          checked={orderNotificationSettings.stop_status_events.arrived}
        />
        <TwCheckbox
          name="stop_complete"
          value="stop_complete"
          label="Marked Complete"
          onInputChange={handleStopStatusCheckboxChange}
          checked={orderNotificationSettings.stop_status_events.complete}
        />
      </Stack>
      <Stack className="load-notifications-options__options-container">
        <Typography className="settings__subtitle">{`Send a stop status update email when a stop type is:`}</Typography>
        <TwCheckbox
          name="pickup_stops"
          value="pickup_stops"
          label="Pickup stops"
          onInputChange={handleStopTypeCheckboxChange}
          checked={orderNotificationSettings.stop_types.pickup}
        />
        <TwCheckbox
          name="drop_off_stops"
          value="drop_off_stops"
          label="Drop-off stops"
          onInputChange={handleStopTypeCheckboxChange}
          checked={orderNotificationSettings.stop_types.drop_off}
        />
        <TwCheckbox
          name="transfer_stops"
          value="transfer_stops"
          label="Transfer stops"
          onInputChange={handleStopTypeCheckboxChange}
          checked={orderNotificationSettings.stop_types.transfer}
        />
        <TwCheckbox
          name="no_type_stops"
          value="no_type_stops"
          label="Stops without a specified type"
          onInputChange={handleStopTypeCheckboxChange}
          checked={orderNotificationSettings.stop_types.none}
        />
      </Stack>
    </Box>
  );
};

export default LoadNotificationOptions;
